import { GiftOutlined, PlusOutlined, QuestionCircleOutlined } from '@ant-design/icons'
import { useQueryClient } from '@tanstack/react-query'
import {
  Button,
  Card,
  Col,
  Divider,
  Form,
  FormProps,
  Input,
  message,
  Row,
  Select,
  Tooltip,
} from 'antd'
import { GoldenTicketType } from 'Modules'
import { useCallback } from 'react'

import { AppAdmin } from 'api/app-admin'
import { RewardPoolArguments, useCreateReward } from 'hooks/use-create-reward'
import { onCloseModalProcessing, onOpenModalProcessing } from 'stores/modal.store'
import { parserUnitContract } from 'utils/contract'
import { notifyFormErrors } from 'utils/form-errors'
import { logError, logInfo } from 'utils/logger'

import { PTOSelect } from './pto-select'
import { RaritiesSetup } from './rarities-setup'

export interface CreateRewardForm {
  pto: string
  ownerAddress: string
  asset: 'MEY' | 'USDC'
  rarities: Array<
    GoldenTicketType & {
      amount: number
      ticket: number
      activationDate: Date
    }
  >
}

export const PTORewardsCreate = () => {
  const [form] = Form.useForm()
  const queryClient = useQueryClient()
  const { isLoading, preparePayload, createRewardPoolOnChain } = useCreateReward()

  const createReward: FormProps<CreateRewardForm>['onFinish'] = useCallback(
    async (data: CreateRewardForm) => {
      try {
        onOpenModalProcessing()
        const payload = preparePayload(data)

        // call BE get arguments
        const argsData = await AppAdmin.createRewardPool(payload)
        logInfo('contract args', argsData)

        const args = argsData.data.data as RewardPoolArguments

        const contractAddress = await createRewardPoolOnChain([
          args.PTOAddress,
          args.USDCAddress,
          args.ownerAddress,
          args.rarityIds,
          args.amounts.map((a) => parserUnitContract(a, 6)),
          args.claimStartTimes.map((d) => BigInt(Math.floor(new Date(d).getTime() / 1000))),
        ])
        logInfo('Reward contract address', contractAddress)

        if (args.poolId && contractAddress) {
          await AppAdmin.updateRewardContractAddress({
            rewardPoolId: args.poolId,
            contractAddress,
          })
        } else {
          throw Error()
        }

        message.success('Reward created successfully')
        queryClient.invalidateQueries({ queryKey: ['rewardsPool'] })
        form.resetFields()
        form.setFieldsValue({ rarities: [] })
      } catch (e: any) {
        message.error('Create reward failed')
        logError(e)
      } finally {
        onCloseModalProcessing()
      }
    },
    [preparePayload, createRewardPoolOnChain, form, queryClient]
  )

  const disabledSubmit = isLoading || form.getFieldsError().some(({ errors }) => errors.length > 0)

  return (
    <Card>
      <div className="rewards-create__title">
        <GiftOutlined />
        <h2>Create New Reward</h2>
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={createReward}
        onFinishFailed={notifyFormErrors}
        className="rewards-create__form"
        initialValues={{ remember: true }}
        scrollToFirstError={{
          behavior: 'smooth',
          block: 'center',
          inline: 'center',
        }}
      >
        <Row gutter={12}>
          <Col span={10}>
            <PTOSelect />
          </Col>

          <Col span={10}>
            <Form.Item
              required
              label={
                <>
                  Owner Address
                  <Tooltip
                    title={
                      <div className="tooltip-box">
                        <p className="tooltip-text">
                          The wallet address that will own and distribute these rewards
                        </p>
                      </div>
                    }
                    placement="right"
                  >
                    <QuestionCircleOutlined className="tooltip-icon" />
                  </Tooltip>
                </>
              }
              name="ownerAddress"
              rules={[
                {
                  required: true,
                  message: 'Owner address is required',
                },
              ]}
            >
              <Input className="rewards-create__input" placeholder="0x..." />
            </Form.Item>
          </Col>

          <Col span={4}>
            <Form.Item
              label="Asset"
              className="rewards-create__rarity-item-form-item rewards-create__rarity-item-form-item--asset"
              name="asset"
              required
              initialValue="MEY"
            >
              <Select>
                <Select.Option value="MEY">MEY</Select.Option>
                <Select.Option value="USDC">USDC</Select.Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Divider className="rewards-create__divider" />

        <RaritiesSetup />

        <Button
          type="primary"
          loading={isLoading}
          disabled={disabledSubmit}
          htmlType="submit"
          className="rewards-create__submit-btn"
          icon={<PlusOutlined />}
        >
          Create Reward
        </Button>
      </Form>
    </Card>
  )
}
